import * as React from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { copyArrayWithIndexChanged } from '../../../Utils';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid2';
import { User, UserAccessLevelStrings, UserStatusStrings, deleteUser, getAllUsers, getDuplicateUsers, updateUserData } from '../../../types/User';
import { EditableTable } from '../../../subs/EditableTable';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const UsersAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [duplicateUsers, setDuplicateUsers] = React.useState<User[]>([]);
    const [allData, setallData] = React.useState<User[]>([]);
    const [filterDuplicates, setFilterDuplicates] = React.useState<boolean>(false);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllUsers = async () => {
            const allUsersResponse = await getAllUsers(new AbortController());
            if (allUsersResponse.failed) {
                showUserFeedback(allUsersResponse.errorText);
                return;
            }
            setallData(allUsersResponse.result);

            const duplicateUsersResponse = await getDuplicateUsers(new AbortController());
            if (duplicateUsersResponse.failed) {
                showUserFeedback(duplicateUsersResponse.errorText);
                return;
            }
            setDuplicateUsers(duplicateUsersResponse.result);
        }
        fetchAllUsers();
    }, [showUserFeedback]);

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = allData.find(row => row.id === newRow.id);
        if (!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.status = newRow.status as number;
        changedData.accessLevel = newRow.accessLevel as number;
        changedData.name = newRow.name;
        changedData.wpid = newRow.wpid;
        changedData.email = newRow.email;
        changedData.companyId = newRow.companyId;
        saveHandler(changedData);
        return newRow;
    };

    const saveHandler = async (changedData: User) => {
        const updateUserRequest = await updateUserData(changedData, new AbortController());

        if (updateUserRequest.failed) {
            showUserFeedback(updateUserRequest.errorText);
            return;
        }
        const updateUser = updateUserRequest.result;
        let allUsersLoc = copyArrayWithIndexChanged(allData, allData.findIndex(tc => tc.id === updateUser.id), updateUser);
        setallData(allUsersLoc);
    };

    const processDelete = async (id: number) => {
        const deleteUserRequest = await deleteUser(id, new AbortController());

        if (deleteUserRequest.failed) {
            showUserFeedback(deleteUserRequest.errorText);
            return;
        }
        let allUsersLoc = allData.filter(c => c.id !== id);
        setallData(allUsersLoc);
    };

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'wpId',
            headerName: 'WordPress',
            flex: 1,
            editable: true,
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 3,
            editable: true,
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 3,
            editable: true,
        },
        {
            field: 'companyId',
            headerName: 'Company ID',
            flex: 3,
            editable: true,
        },
        {
            field: 'accessLevel',
            headerName: 'Access Level',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return UserAccessLevelStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return UserAccessLevelStrings[value];
            },
            valueSetter: (value, row) => {
                let accessLevel = 0;
                if (UserAccessLevelStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < UserAccessLevelStrings.length; i++) {
                        if (UserAccessLevelStrings[i] === value) {
                            accessLevel = i;
                            break;
                        }
                    }
                }
                return { ...row, accessLevel: accessLevel };
            },
            type: 'singleSelect',
            valueOptions: UserAccessLevelStrings
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return UserStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return UserStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let status = 0;
                if (UserStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < UserStatusStrings.length; i++) {
                        if (UserStatusStrings[i] === value) {
                            status = i;
                            break;
                        }
                    }
                }
                return { ...row, status: status };
            },
            type: 'singleSelect',
            valueOptions: UserStatusStrings
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Grid size={6} paddingBottom={10}>
            <FormControlLabel key={"filterDuplicateFlag"}
                control={<Checkbox checked={filterDuplicates}
                    onChange={(e) => { setFilterDuplicates(e.target.checked); }}
                    inputProps={{ 'aria-label': 'controlled' }} />} label="Filter Duplicate Users" />
        </Grid>
        <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
            <EditableTable
                columns={columns}
                rows={filterDuplicates ? duplicateUsers : allData}
                setAllRows={setallData}
                processRowUpdate={processRowUpdate}
                processRowDelete={processDelete}
            />
        </Box>
    </React.Fragment >
}